import React, { useEffect, useState } from 'react';
import TextInput from '@common/components/Inputs/TextInput';
import Stack from '@mui/material/Stack';
import FleetDashboard from 'models/fleet-management/FleetDashboard';
import { useAppDispatch, useAppSelector } from 'store';
import {
  createNewFleetDashboard,
  deleteDashboardById,
  saveFleetDashboard,
} from 'store/fleet-management/fleet-management-actions';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import { selectFleetDashboardsRestState } from 'store/fleet-management/fleet-management-slice-selectors';
import { patchDashboardsRestState } from 'store/fleet-management/fleet-management-slice';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import OptionSwitchInput from '@common/components/Inputs/OptionSwitchInput';
import CustomFleetDashboardIcon, { FleetDashboardIcon } from './CustomFleetDashboardIcon';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import {
  CollapseDownArrow,
  CrossCloseIcon,
  CustomTuneFiltersIcon,
  DashboardIcon,
  SaveIcon,
} from '@common/utils/icons/icons';
import { useTranslation } from 'react-i18next';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import WidgetsForm from './widget/WidgetsForm';
import FleetWidget, { widgetDiffersToMainList } from 'models/fleet-management/FleetWidget';
import ConfirmAbortDialog from '@common/components/General/ConfirmAbortDialog';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CustomAlert from '@common/components/Feedback/CustomAlert';

const DashboardForm: React.FC<{
  dashboard: FleetDashboard;
  onClose: () => void;
  editingWidgets: {
    id: string | null;
    widgets: FleetWidget[];
    onChange: (widgets: FleetWidget[]) => void;
    onSelectedChange: (id: string | null) => void;
  };
  duplicate?: boolean;
}> = ({ dashboard, editingWidgets, duplicate, onClose }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const rest = useAppSelector((state) => selectFleetDashboardsRestState(state));

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isDashboardConfigOpen, setIsDashboardConfigOpen] = useState(true);

  useEffect(() => {
    if (
      rest.postStatus === HttpFetchStatus.Success ||
      rest.patchStatus === HttpFetchStatus.Success ||
      rest.deleteStatus === HttpFetchStatus.Success
    ) {
      onClose();
    }
  }, [rest.patchStatus, rest.postStatus, onClose, rest.deleteStatus]);

  const { resetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: rest.postError,
      resetPostHandler: () =>
        dispatch(patchDashboardsRestState({ postStatus: HttpFetchStatus.Idle, postError: null })),
      patchError: rest.patchError,
      resetPatchHandler: () =>
        dispatch(patchDashboardsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
    });

  const [icon, setIcon] = useState(dashboard.icon);

  const { value: name, valueChangeHandler: nameChangeHandler } = useValidatedInputEffect(
    dashboard.name ?? '',
    (_) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('name'),
    removeServerError,
    resetToggle,
  );

  const { value: description, valueChangeHandler: descriptionChangeHandler } =
    useValidatedInputEffect(
      dashboard.description ?? '',
      (_) => ({ isValid: true, error: null }),
      (_) => () => {},
      getServerValidationResultOrNull('description'),
      removeServerError,
      resetToggle,
    );

  const iconChangeHandler = (newIcon: string) => {
    setIcon(newIcon as FleetDashboardIcon);
  };

  const resetDashboardRestStateHandler = () => {
    dispatch(
      patchDashboardsRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      }),
    );
  };

  const saveClickHandler = () => {
    if (dashboard == null || editingWidgets == null) return;

    if (duplicate) {
      dispatch(
        createNewFleetDashboard({
          ...dashboard,
          id: undefined,
          name,
          description,
          icon,
          isDefault: false,
          widgets: editingWidgets.widgets.map((w) => ({
            ...w,
            filters: w.filters.map((f) => ({
              ...f,
              id: undefined,
            })),
            id: undefined,
          })),
        }),
      );
    } else {
      dispatch(
        saveFleetDashboard({
          ...dashboard,
          name,
          description,
          icon,
          widgets: editingWidgets.widgets,
        }),
      );
    }
  };

  const toggleDashboardConfigurationsClickHandler = () => {
    setIsDashboardConfigOpen((prevState) => !prevState);
  };

  const deleteClickHandler = () => {
    setIsConfirmDeleteOpen(true);
  };

  const abortDeleteHandler = () => {
    setIsConfirmDeleteOpen(false);
  };

  const confirmDeleteHandler = () => {
    setIsConfirmDeleteOpen(false);
    dispatch(deleteDashboardById(dashboard.id));
  };

  const unselectWidgetClickHandler = () => {
    if (editingWidgets != null) {
      editingWidgets.onSelectedChange(null);
    }
  };

  const unsavedChanges =
    editingWidgets.widgets.length !== dashboard.widgets.length ||
    editingWidgets.widgets.some((w) => widgetDiffersToMainList(w, dashboard.widgets)) ||
    icon !== dashboard.icon ||
    description !== dashboard.description ||
    name !== dashboard.name;

  return (
    <React.Fragment>
      <Stack
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          height: '100%',
          overflow: 'hidden',
          width: theme.spacing(48),
        })}
      >
        <Stack flexGrow={1} sx={{ width: '100%', overflowX: 'hidden', overflowY: 'auto', px: 2 }}>
          <Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={(theme) => ({ height: theme.spacing(8) })}
            >
              <DashboardIcon />
              <Typography flex={1} variant="h2">
                {t('title-dashboard-properties')}
              </Typography>
              <IconButton onClick={toggleDashboardConfigurationsClickHandler}>
                <CollapseDownArrow
                  sx={{
                    transform: isDashboardConfigOpen ? undefined : 'rotate(180deg)',
                    transition: 'transform 0.3s ease-in-out',
                  }}
                />
              </IconButton>
            </Stack>
            <Collapse in={isDashboardConfigOpen}>
              <Stack spacing={2}>
                <OptionSwitchInput
                  options={Object.values(FleetDashboardIcon).map((i) => ({
                    value: i,
                    label: <CustomFleetDashboardIcon icon={i} />,
                  }))}
                  value={icon}
                  onChange={iconChangeHandler}
                />
                <Stack>
                  <InputLabel>{t('input-label-name')}</InputLabel>
                  <TextInput size="small" value={name} onChange={nameChangeHandler} />
                </Stack>
                <Stack>
                  <InputLabel>{t('input-label-description')}</InputLabel>
                  <TextInput size="small" value={description} onChange={descriptionChangeHandler} />
                </Stack>
                {!duplicate && (
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button size="large" color="error" onClick={deleteClickHandler}>
                      {t('button-delete-dashboard')}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Collapse>
          </Stack>

          <Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={(theme) => ({ height: theme.spacing(8) })}
            >
              <CustomTuneFiltersIcon />
              <Typography flex={1} variant="h2">
                {t('title-widget-properties')}
              </Typography>
              {editingWidgets?.id != null && (
                <IconButton onClick={unselectWidgetClickHandler}>
                  <CrossCloseIcon />
                </IconButton>
              )}
            </Stack>
            {editingWidgets?.id == null ? (
              <CustomAlert severity="info" header={t('info-select-widget-or-create-one')} />
            ) : (
              <WidgetsForm
                selectedId={editingWidgets.id}
                widgets={editingWidgets.widgets}
                onChange={editingWidgets.onChange}
              />
            )}
          </Stack>
        </Stack>
        <Stack
          sx={{
            width: '100%',
          }}
        >
          <Button
            size="large"
            fullWidth
            startIcon={<SaveIcon />}
            onClick={saveClickHandler}
            disabled={!unsavedChanges}
          >
            {t('button-save')}
          </Button>
        </Stack>
      </Stack>

      <ConfirmAbortDialog
        isOpen={isConfirmDeleteOpen}
        title={t('title-delete-dashboard')}
        description={t('info-delete-dashboard')}
        abortButtonText={t('button-cancel')}
        confirmButtonText={t('button-confirm')}
        onConfirm={confirmDeleteHandler}
        onAbort={abortDeleteHandler}
      />
      <CustomSnackbar
        open={rest.patchStatus === HttpFetchStatus.Success}
        title={t('success-saving-dashboard')}
        onClose={resetDashboardRestStateHandler}
      />
      <CustomSnackbar
        open={rest.postStatus === HttpFetchStatus.Success}
        title={t('success-creating-dashboard')}
        onClose={resetDashboardRestStateHandler}
      />
      <CustomSnackbar
        open={rest.deleteStatus === HttpFetchStatus.Success}
        title={t('success-deleting-dashboard')}
        onClose={resetDashboardRestStateHandler}
      />
    </React.Fragment>
  );
};

export default DashboardForm;
